import React, { useState, useEffect } from "react";
import Skeleton from "react-loading-skeleton";
import { useTranslation } from "react-i18next";
import Plyr from "plyr-react";
import "plyr-react/plyr.css";
import type {
  TransformedProduct,
  TransformedVariation,
  EnrichedAttributeTransformed,
} from "../../types/productApiTypes";

// --- Props ---
interface ProductPanelsProps {
  product: TransformedProduct;
  isLoading: boolean;
  selectedVariation?: TransformedVariation | null;
}

// --- Component ---
const ProductPanels: React.FC<ProductPanelsProps> = ({
  product,
  isLoading,
  selectedVariation = null,
}) => {
  const { t } = useTranslation();

  // This state ensures we only render the Plyr component on the client-side after the initial mount.
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // This effect runs only once on the client after the component has mounted.
    setIsClient(true);
  }, []);

  const { description, enrichedAttributes = [], productVideoUrl } = product;

  const displayDescription = selectedVariation?.description || description;

  const hasVideo = !!productVideoUrl;
  const hasDescription = displayDescription && displayDescription.trim() !== "";
  const hasAttributes = enrichedAttributes.length > 0;

  const [activePanel, setActivePanel] = useState<string | null>(
    hasVideo ? "video" : null
  );

  const handlePanelClick = (panelId: string) => {
    setActivePanel(activePanel === panelId ? null : panelId);
  };

  if (isLoading) {
    return <ProductPanelsSkeleton />;
  }

  return (
    <div className="product-sections">
      {/* Product Video Section with Plyr */}
      {hasVideo && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "video" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("video")}
            aria-expanded={activePanel === "video"}
            aria-controls="video-panel"
            id="video-header"
          >
            {t("product.productVideo")}
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div
            id="video-panel"
            className={`panel-content ${
              activePanel === "video" ? "active" : ""
            }`}
            role="region"
            aria-labelledby="video-header"
          >
            <div className="panel-content__inner">
              {isClient && activePanel === "video" && (
                <div className="video-player-container">
                  <Plyr
                    source={{
                      type: "video",
                      sources: [
                        {
                          src: productVideoUrl!,
                          provider: productVideoUrl!.includes("youtube")
                            ? "youtube"
                            : "vimeo",
                        },
                      ],
                    }}
                    options={{
                      muted: true,
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Description Section */}
      {hasDescription && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "description" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("description")}
            aria-expanded={activePanel === "description"}
            aria-controls="description-panel"
            id="description-header"
          >
            {t("product.description")}
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div
            id="description-panel"
            className={`panel-content ${
              activePanel === "description" ? "active" : ""
            }`}
            role="region"
            aria-labelledby="description-header"
          >
            <div className="panel-content__inner">
              <div dangerouslySetInnerHTML={{ __html: displayDescription }} />
            </div>
          </div>
        </div>
      )}

      {/* Properties Section */}
      {hasAttributes && (
        <div className="product-section">
          <button
            type="button"
            className={`product-section-header ${
              activePanel === "properties" ? "active" : ""
            }`}
            onClick={() => handlePanelClick("properties")}
            aria-expanded={activePanel === "properties"}
            aria-controls="properties-panel"
            id="properties-header"
          >
            {t("product.properties")}
            <span className="toggle-icon" aria-hidden="true"></span>
          </button>
          <div
            id="properties-panel"
            className={`panel-content ${
              activePanel === "properties" ? "active" : ""
            }`}
            role="region"
            aria-labelledby="properties-header"
          >
            <div className="panel-content__inner">
              <table className="product-attributes">
                <tbody>
                  {enrichedAttributes.map(
                    (attribute: EnrichedAttributeTransformed) => {
                      const termNames = attribute.terms.map(
                        (term) => term.name
                      );
                      if (termNames.length === 0) return null;
                      return (
                        <tr key={attribute.id}>
                          <th>{attribute.name}</th>
                          <td>{termNames.join(", ")}</td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* REMOVED: Reviews Section */}
    </div>
  );
};

// --- Skeleton Component ---
const ProductPanelsSkeleton: React.FC = () => {
  const sectionCount = 3;
  return (
    <div className="product-sections-loading">
      {[...Array(sectionCount)].map((_, index) => (
        <div className="product-section-skeleton" key={index}>
          <Skeleton height={45} className="section-header-skeleton" />
        </div>
      ))}
    </div>
  );
};

// --- Export with Skeleton ---
type ProductPanelsComponent = React.FC<ProductPanelsProps> & {
  Skeleton: React.FC<{}>;
};
(ProductPanels as ProductPanelsComponent).Skeleton = ProductPanelsSkeleton;
export default ProductPanels as ProductPanelsComponent;
